import { get as getConfig } from '../../../../../config/index'
import {
  client as apolloClient,
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
} from '../../../../apollo-graphql/index'
import { getAuthIsLoggedIn } from '../../../../auth/index'
import { log as logError } from '../../../../../log/error'
import type { Avatar } from '../../../types'
import { selectBackfillUids } from './backfill-uids'
import { SET_AUTH_DATA } from '../../../../auth/actions'
import { USER_PROFILES_SET } from '../../../../user-profiles/actions'
import {
  CREATE_PROFILE_PICTURE_UPLOAD,
  CREATE_PROFILE_PICTURE_CONFIRM,
  DELETE_PROFILE_PICTURE,
  GET_CURRENT_PROFILE_LIGHT,
  GET_PROFILE_IDENTITY_BY_UID,
} from '../../../query'
import {
  CommunityReduxState,
  isStringMap,
  selectAuthJwt,
  selectAuthUid,
  selectProfilePictureForUid,
  selectProfilePicturesData,
  selectUserProfilesDataMap,
} from './state-types'
import * as actions from './actions'

const watcherConfig = getConfig()
const communityProfileEnabled = !!(watcherConfig && watcherConfig.features
  && watcherConfig.features.communityProfile)

const CONTENT_TYPE_MAP: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
}

// ── Watcher framework interfaces ─────────────────────────────────────
// Exported so tests can assign function variables typed against the same
// `WatcherConfig` shape this module's watcher exports declare. Without
// the shared identity, TS reports "Two different types with this name
// exist, but they are unrelated" at the test's variable-assignment site.

export type WatcherState = CommunityReduxState

export interface WatcherAction<P = unknown> {
  type: string
  payload: P
}

export interface WatcherContext<P = unknown> {
  dispatch: (action: { type: string; payload?: unknown }) => void
  state: WatcherState
  action: WatcherAction<P>
}

export interface WatcherChain {
  when: (predicate: (ctx: { state: WatcherState }) => boolean) => void
}

export interface WatcherConfig {
  after: <P = unknown>(
    actionType: string,
    handler: (ctx: WatcherContext<P>) => Promise<void> | void,
  ) => WatcherChain
}

// ── Watchers ─────────────────────────────────────────────────────────

/**
 * Upload a profile picture via a presigned S3 URL.
 * Flow: requestProfilePictureUpload -> PUT to S3 -> confirmProfilePictureUpload
 */
export function watchUploadAvatar ({ after }: WatcherConfig) {
  return after(
    actions.COMMUNITY_PROFILE_UPLOAD_AVATAR,
    async ({ dispatch, state, action }:
    WatcherContext<{ imageBlob: Blob; fileExtension: string }>) => {
      dispatch(actions.setAvatarUploadProcessing(true))
      try {
        const { imageBlob, fileExtension } = action.payload
        const authToken = selectAuthJwt(state)

        // 1. Request a presigned upload URL
        const { data } = await apolloClient.mutate({
          mutation: CREATE_PROFILE_PICTURE_UPLOAD,
          variables: { fileExtension: fileExtension || 'png' },
          context: { jwt: authToken },
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
        })
        const uploadRequest = data.requestProfilePictureUpload
        const contentType = CONTENT_TYPE_MAP[fileExtension] || 'image/png'

        // 2. Upload the image to S3
        const uploadResponse = await fetch(uploadRequest.uploadUrl, {
          method: 'PUT',
          body: imageBlob,
          headers: { 'Content-Type': contentType },
        })
        if (!uploadResponse.ok) {
          throw new Error(`Profile image S3 upload failed: ${uploadResponse.status}`)
        }

        // 3. Confirm the upload
        const confirmResult = await apolloClient.mutate({
          mutation: CREATE_PROFILE_PICTURE_CONFIRM,
          variables: {
            imageUuid: uploadRequest.imageUuid,
            imageExtension: fileExtension || 'png',
          },
          context: { jwt: authToken },
        })
        const confirmed = confirmResult.data.confirmProfilePictureUpload

        dispatch(actions.setAvatarUploadSuccess())

        // Update Redux directly from the confirm response to bypass the
        // fetchProfilePictures cache-hit check (uid is already cached).
        const uid = selectAuthUid(state)
        if (uid && confirmed.profilePicture) {
          dispatch(actions.setProfilePictures({
            [uid]: confirmed.profilePicture,
          }))
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        logError(error, 'Community profile avatar upload failed')
        dispatch(actions.setAvatarUploadError(error.message))
      } finally {
        dispatch(actions.setAvatarUploadProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

/**
 * Delete the current user's profile picture via GraphQL.
 */
export function watchDeleteAvatar ({ after }: WatcherConfig) {
  return after(
    actions.COMMUNITY_PROFILE_DELETE_AVATAR,
    async ({ dispatch, state }: WatcherContext) => {
      dispatch(actions.setAvatarDeleteProcessing(true))
      try {
        const authToken = selectAuthJwt(state)

        await apolloClient.mutate({
          mutation: DELETE_PROFILE_PICTURE,
          context: { jwt: authToken },
        })
        dispatch(actions.setAvatarDeleteSuccess())

        // Clear the cached avatar so the UI immediately reflects removal.
        const uid = selectAuthUid(state)
        if (uid) {
          dispatch(actions.setProfilePictures({
            [uid]: { url: '', small: '', card: '' },
          }))
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        logError(error, 'Community profile avatar delete failed')
        dispatch(actions.setAvatarDeleteError(error.message))
      } finally {
        dispatch(actions.setAvatarDeleteProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

/**
 * Fetch profile pictures via the UserProfile GraphQL query.
 * Loads the current user's avatar from userProfileCurrent and caches
 * it in Redux so the nav bar, edit page, and comment input render it.
 *
 * Dedup: callers dispatch the full set of uids they need rendered
 * (see watchUserProfilesForPictures, ManageProfilesPage, MenuUser). When
 * every requested uid already has a cached picture, the network-only fetch
 * is skipped to avoid a redundant refetch on each mount.
 */
export function watchFetchProfilePictures ({ after }: WatcherConfig) {
  return after(
    actions.COMMUNITY_PROFILE_FETCH_PROFILE_PICTURES,
    async ({ dispatch, state, action }:
    WatcherContext<{ uids: Array<number | string> }>) => {
      if (!communityProfileEnabled) return

      // Skip the fetch entirely when all requested uids are already cached.
      // (No uids in the payload preserves today's current-user fetch.)
      const requestedUids = action.payload?.uids
      if (Array.isArray(requestedUids) && requestedUids.length > 0) {
        const cachedPictures = selectProfilePicturesData(state)
        const hasUncachedUid = requestedUids
          .some((uid) => !cachedPictures.has(String(uid)))
        if (!hasUncachedUid) return
      }

      dispatch(actions.setProfilePicturesProcessing(true))
      try {
        const jwt = selectAuthJwt(state)
        if (!jwt) return

        // `userProfileCurrent` resolves the VIEWER only, so this call fills the signed-in
        // profile; the other profiles on the account are backfilled per uid below.
        const { data } = await apolloClient.query({
          query: GET_CURRENT_PROFILE_LIGHT,
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
          context: { jwt },
        })

        const profile = data?.userProfileCurrent
        const currentUid = profile?.uid
          ? String(profile.uid)
          : String(selectAuthUid(state) || '')
        if (profile?.uid && profile?.avatar) {
          dispatch(actions.setProfilePictures({
            [String(profile.uid)]: {
              ...profile.avatar,
              displayName: profile.displayName ?? null,
            },
          }))
        }

        // F176 Part 2, now wired: fill the OTHER profiles on the account.
        //
        // Without this the household's other profiles fall back to the ACCOUNT name
        // (`AccountMemberProfile.displayName` is "firstName or username", not the Community
        // display name) and the default avatar — the old pre-Community presentation sitting
        // next to the new one in the same picker.
        //
        // ⚠️ Per-uid because no by-uids profile query exists; see GET_PROFILE_IDENTITY_BY_UID.
        // MAX_PROFILES is 3, and already-cached uids are filtered out, so this is at most two
        // extra calls and normally zero on repeat renders.
        const cachedPictures = selectProfilePicturesData(state)
        const backfillUids = selectBackfillUids(
          requestedUids,
          currentUid,
          (uid) => cachedPictures.has(uid),
        )

        if (backfillUids.length > 0) {
          const identities = await Promise.all(backfillUids.map(async (uid) => {
            try {
              const { data: otherData } = await apolloClient.query({
                query: GET_PROFILE_IDENTITY_BY_UID,
                variables: { uid },
                fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
                context: { jwt },
              })
              return otherData?.userProfileByUid || null
            } catch (err) {
              // One unreachable profile must not blank the rest of the picker. A profile with
              // no Community row simply keeps the account-name fallback it has today.
              logError(
                err instanceof Error ? err : new Error(String(err)),
                `Profile identity fetch failed for uid ${uid}`,
              )
              return null
            }
          }))

          const backfill = identities.reduce((acc, other) => {
            if (!other?.uid) return acc
            acc[String(other.uid)] = {
              ...(other.avatar || {}),
              displayName: other.displayName ?? null,
            }
            return acc
          }, {} as Record<string, Avatar & { displayName?: string | null }>)

          if (Object.keys(backfill).length > 0) {
            dispatch(actions.setProfilePictures(backfill))
          }
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        logError(error, 'Profile pictures fetch failed')
        dispatch(actions.setProfilePicturesError(error.message))
      } finally {
        dispatch(actions.setProfilePicturesProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

/**
 * When user profiles are loaded (multi-profile list), automatically
 * fetch profile pictures for all uids via GraphQL.
 */
export function watchUserProfilesForPictures ({ after }: WatcherConfig) {
  return after(
    USER_PROFILES_SET,
    async ({ dispatch, state }: WatcherContext) => {
      if (!communityProfileEnabled) return
      const profiles = selectUserProfilesDataMap(state)
      if (!profiles || profiles.size === 0) return

      const uids: Array<string | number> = []
      profiles.forEach((profile) => {
        if (!isStringMap(profile)) return
        const uid = profile.get('uid')
        if (typeof uid === 'string' || typeof uid === 'number') uids.push(uid)
      })

      if (uids.length > 0) {
        dispatch(actions.fetchProfilePictures(uids))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

/**
 * When auth data is set (login / page load), fetch the current user's
 * profile picture so the nav-bar avatar renders immediately.
 */
export function watchAuthForCurrentProfilePicture ({ after }: WatcherConfig) {
  return after(
    SET_AUTH_DATA,
    async ({ dispatch, state }: WatcherContext) => {
      if (!communityProfileEnabled) return
      const uid = selectAuthUid(state)
      if (!uid) return

      const cached = selectProfilePictureForUid(state, uid)
      if (cached && cached.size > 0) return

      dispatch(actions.fetchProfilePictures([uid]))
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}
